import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function CountsTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ sku: "", bin: "", counted_qty: "", remarks: "" });
  const canVerify = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);

  const load = () => api.get("/stock/counts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/stock/counts", { sku: form.sku, bin: form.bin || null, counted_qty: parseInt(form.counted_qty) || 0, remarks: form.remarks || null });
      toast.success("Count submitted — stock NOT changed until verified & adjusted");
      setForm({ sku: "", bin: "", counted_qty: "", remarks: "" });
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  const verify = async (id) => {
    try {
      const r = await api.post(`/stock/counts/${id}/verify`, {});
      toast.success(r.data.adjustment_id ? `Verified — adjustment ${r.data.adjustment_id} created for approval` : "Verified — no variance");
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  return (
    <div className="space-y-4" data-testid="counts-tab">
      <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        <Input placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="cnt-sku" />
        <Input placeholder="Bin (optional)" value={form.bin} onChange={(e) => setForm({ ...form, bin: e.target.value })} data-testid="cnt-bin" />
        <Input type="number" placeholder="Counted qty" value={form.counted_qty} onChange={(e) => setForm({ ...form, counted_qty: e.target.value })} data-testid="cnt-qty" />
        <Input placeholder="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="cnt-remarks" />
        <Button onClick={submit} className="bg-primary text-primary-foreground" data-testid="cnt-submit">Submit Count</Button>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="counts-table">
          <thead><tr className="border-b border-border text-left">
            {["ID", "SKU", "Bin", "Counted", "System", "Variance", "Counted By", "Status", "Adjustment", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.count_id} className="border-b border-border/50" data-testid={`cnt-row-${c.count_id}`}>
                <td className="px-4 py-2.5 metric text-xs">{c.count_id}</td>
                <td className="px-4 py-2.5 metric font-bold">{c.sku}</td>
                <td className="px-4 py-2.5 metric text-xs">{c.bin || "—"}</td>
                <td className="px-4 py-2.5 metric">{c.counted_qty}</td>
                <td className="px-4 py-2.5 metric">{c.system_qty}</td>
                <td className={`px-4 py-2.5 metric font-bold ${c.variance === 0 ? "text-signal-green" : "text-signal-red"}`}>{c.variance > 0 ? "+" : ""}{c.variance}</td>
                <td className="px-4 py-2.5 text-xs">{c.counted_by}</td>
                <td className="px-4 py-2.5"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${c.status === "verified" ? "bg-signal-green/15 text-signal-green" : "bg-signal-amber/15 text-signal-amber"}`}>{c.status}</span></td>
                <td className="px-4 py-2.5 metric text-xs">{c.adjustment_id || "—"}</td>
                <td className="px-4 py-2.5">
                  {c.status === "pending_verify" && canVerify && c.counted_by_id !== user?.user_id && (
                    <Button size="sm" variant="outline" onClick={() => verify(c.count_id)} data-testid={`cnt-verify-${c.count_id}`}>Verify</Button>)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No counts</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
